import React, { useEffect, useMemo, useState } from 'react';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Grid, { GridItem } from '../../components/layout/Grid';
import { useQuery } from 'react-query';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import AlertBox from '../../components/layout/AlertBox';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoModeloMensagem from '@/components/Formulario/CampoModeloMensagem';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoUpload from '@/components/Formulario/CampoUpload';
import Botao from '@/components/Botao';
import HBox from '@/components/layout/HBox';
import { mdiPlus, mdiTrashCan } from '@mdi/js';
import { nomesParametros, nomesParametrosInvalidos } from '@tera/shared/src/lib/modeloMensagem';

type UsoParametros = { id: number; nome?: string; parametros: string[] };

/** Fluxos/campanhas que alimentam parâmetros que saíram do texto, ou não alimentam os novos */
function usosAfetados(usos: UsoParametros[] | undefined, atuais: string[]) {
  return (usos ?? [])
    .map(uso => ({
      ...uso,
      removidos: uso.parametros.filter(p => !atuais.includes(p)),
      novos: atuais.filter(p => !uso.parametros.includes(p)),
    }))
    .filter(uso => uso.removidos.length || uso.novos.length);
}

export const FormMensagemProgramada = ({
  value,
  onChange,
  fieldErrors,
}: {
  value: any;
  onChange: any;
  fieldErrors: any;
}) => {
  const newObj = !value?.id || value?.id < 0;

  // A Meta só aceita edição de modelos aprovados, rejeitados ou pausados.
  // Enquanto o modelo está em avaliação, o conteúdo fica travado.
  const bloqueado = !newObj && !['aprovada', 'rejeitada'].includes(value?.estado);
  // A categoria de um modelo já aprovado não pode ser trocada
  const categoriaBloqueada = !newObj && (bloqueado || value?.estado === 'aprovada');

  const canaisQuery = useQuery(['canal'], async () => {
    const res = await fetch('/api/canal/box');
    return res.json();
  });

  const canaisOptions =
    canaisQuery.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) || [];

  const paramsList = nomesParametros(value?.conteudo);
  const paramsInvalidos = nomesParametrosInvalidos(value?.conteudo);
  const paramsValid = paramsList.length > 0 && paramsInvalidos.length === 0;

  const usoQuery = useQuery(
    ['mensagem_programada_uso', value?.id],
    async () => {
      const res = await fetch(`/api/mensagem_programada/${value.id}/uso`);
      return (await res.json()).result as { fluxos: UsoParametros[]; campanhas: UsoParametros[] };
    },
    { enabled: !newObj },
  );
  const fluxosAfetados = usosAfetados(usoQuery.data?.fluxos, paramsList);
  const campanhasAfetadas = usosAfetados(usoQuery.data?.campanhas, paramsList);

  const selectedCanal = canaisQuery.data?.result?.find((it: any) => it.id === value?.canal_id);

  // Normalização de campos: a API pode enviar "cabecalho_tipo" e "cabecalho_texto"
  // enquanto o formulário usa "tipo_cabecalho" e "cabecalho" em criação.
  // Aqui unificamos para evitar quebra na edição.
  const tipoCabecalho: string = (value?.tipo_cabecalho ?? value?.cabecalho_tipo ?? 'nenhum') as string;
  const cabecalhoTexto: string | undefined = (value?.cabecalho_texto ?? value?.cabecalho) as string | undefined;

  // Preview do cabeçalho (mídia)
  const [localObjectUrl, setLocalObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (value?.cabecalho_arquivo instanceof File) {
      const url = URL.createObjectURL(value.cabecalho_arquivo);
      setLocalObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setLocalObjectUrl(null);
  }, [value?.cabecalho_arquivo]);

  const existingHeaderUrl = useMemo(() => {
    if (value?.id > 0 && cabecalhoTexto && ['imagem', 'video', 'documento'].includes(tipoCabecalho)) {
      const filename = encodeURIComponent(cabecalhoTexto);
      return `/api/assets/mensagem_programada/${value.id}/${filename}`;
    }
    return null;
  }, [value?.id, cabecalhoTexto, tipoCabecalho]);

  const headerPreviewUrl = localObjectUrl ?? existingHeaderUrl;

  return (
    <>
      {bloqueado && (
        <AlertBox variant="warning">
          <p>
            Este modelo está em avaliação pela Meta e não pode ser editado até o
            fim da análise.
          </p>
        </AlertBox>
      )}

      {!newObj && !bloqueado && (
        <AlertBox variant="info">
          <p>
            Ao salvar, o modelo volta para avaliação da Meta e só poderá ser
            usado novamente após a aprovação.
          </p>
          <p>
            Modelos aprovados podem ser editados até 10 vezes em 30 dias e no
            máximo 1 vez a cada 24 horas.
            {value?.estado === 'aprovada' &&
              ' A categoria de um modelo aprovado não pode ser alterada.'}
          </p>
        </AlertBox>
      )}

      <Grid>
        <div style={{ position: 'absolute', height: 0 }}>
          <input
            type="text"
            name="username"
            style={{
              height: 0,
              width: 0,
              border: 'none',
              outline: 'none',
              margin: 0,
              padding: 0,
            }}
          />
          <input
            type="password"
            name="password"
            style={{
              height: 0,
              width: 0,
              border: 'none',
              outline: 'none',
              margin: 0,
              padding: 0,
            }}
          />
        </div>

        <GridItem sm={12} md={3}>
          <CampoSeletor
            instanceId='canal'
            disabled={value?.id > 0}
            label="Canal"
            value={value?.canal_id}
            onChange={v => onChange({ ...value, canal_id: v })}
            options={canaisOptions}
            optionLabel="nome"
            optionValue="id"
          />
        </GridItem>

        <GridItem sm={12} md={6}>
          <CampoTexto
            instanceId='nome'
            label="Nome"
            value={value?.nome}
            onChange={v => onChange({ ...value, nome: v })}
          />
        </GridItem>

        <GridItem sm={12} md={3}>
          <CampoSeletor
            instanceId='categoria'
            noSearch
            disabled={categoriaBloqueada}
            label="Categoria"
            value={value?.categoria}
            onChange={v => onChange({ ...value, categoria: v })}
            options={[
              { id: 'notificacao', label: 'Notificação' },
              { id: 'marketing', label: 'Marketing' },
              { id: 'autenticacao', label: 'Autenticação' },
            ]}
          />
        </GridItem>

        {(['whatsapp'].includes(selectedCanal?.tipo)) && (<GridItem sm={12}>
          <CampoSeletor
            noSearch
            options={[
              { id: 'texto', label: 'Solicitar conversa' },
              { id: 'ligacao', label: 'Solicitar ligação' },
            ]}
            instanceId="formato_interacao"
            label="Formato de interação"
            value={value?.formato_interacao || 'texto'}
            onChange={v => onChange({ ...value, formato_interacao: v })}
            disabled={bloqueado}
          />
        </GridItem>)}

        {(['whatsapp'].includes(selectedCanal?.tipo)) && (
          <>
            <GridItem sm={12} md={12}>
              <CampoSeletor
                instanceId='tipo_cabecalho'
                noSearch
                disabled={bloqueado}
                label="Tipo de Cabeçalho"
                value={tipoCabecalho}
                onChange={v => onChange({
                  ...value,
                  tipo_cabecalho: v,
                  // conteúdo do cabeçalho anterior não serve para o novo tipo
                  cabecalho_texto: null,
                  cabecalho_arquivo: undefined,
                })}
                options={[
                  { id: 'nenhum', label: 'Nenhum' },
                  { id: 'texto', label: 'Texto' },
                  { id: 'imagem', label: 'Imagem' },
                  { id: 'video', label: 'Vídeo' },
                  { id: 'documento', label: 'Documento' },
                ]}
              />

              {tipoCabecalho === 'texto' && (
                <CampoMemo
                  instanceId='cabecalho'
                  disabled={bloqueado}
                  label="Cabeçalho (texto)"
                  value={cabecalhoTexto}
                  onChange={v => onChange({ ...value, cabecalho_texto: v })}
                  error={fieldErrors?.cabecalho}
                  min={1}
                  max={120}
                />
              )}

              {['imagem', 'video', 'documento'].includes(tipoCabecalho) && (
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: 4 }}>Arquivo do cabeçalho</label>
                  {!bloqueado && (
                    <CampoUpload
                      instanceId="cabecalho_arquivo"
                      label="Selecione um arquivo"
                      onChange={(files) => onChange({ ...value, cabecalho_arquivo: files[0] })}
                      value={value.cabecalho_arquivo ? [value.cabecalho_arquivo] : []}
                      maxFiles={1}
                      accept={
                        tipoCabecalho === 'imagem' ? 'image/*' :
                          tipoCabecalho === 'video' ? 'video/*' :
                            tipoCabecalho === 'documento' ? '.pdf,.doc,.docx,.xls,.xlsx,.txt' : undefined
                      }
                      error={fieldErrors?.cabecalho_arquivo}
                      size="sm"
                    />
                  )}

                  {/* Prévia do cabeçalho (mídia) */}
                  {headerPreviewUrl && (
                    <div style={{ marginTop: '8px' }}>
                      {tipoCabecalho === 'imagem' && (
                        <img src={headerPreviewUrl} alt="imagem do cabeçalho" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: 8 }} />
                      )}
                      {tipoCabecalho === 'video' && (
                        <video controls style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: 8 }}>
                          <source src={headerPreviewUrl} type="video/mp4" />
                        </video>
                      )}
                      {tipoCabecalho === 'documento' && (
                        <a href={headerPreviewUrl} target="_blank" rel="noreferrer">
                          {cabecalhoTexto || (value?.cabecalho_arquivo instanceof File ? value?.cabecalho_arquivo?.name : 'Documento do cabeçalho')}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </GridItem>
          </>
        )}
        <GridItem sm={12} md={12}>
          <CampoMemo
            instanceId='conteudo'
            disabled={bloqueado}
            label="Texto"
            value={value?.conteudo}
            onChange={v => onChange({ ...value, conteudo: v })}
            error={fieldErrors?.conteudo}
            min={25}
            max={500}
          />
        </GridItem>
      </Grid>

      {!value?.id && paramsList.length === 0 && (
        <AlertBox variant="info">
          <p>
            Dica: Você pode adicionar parâmetros para inserir dados depois, como
            nomes, telefones etc.
          </p>
          <p>
            Os parâmetros ficam entre chaves duplas ( &#123;&#123; &#125;&#125;
            ) e têm nome com letras minúsculas sem acento, números e "_".
          </p>
          <p>
            Exemplo: Olá, &#123;&#123;nome&#125;&#125;! O evento
            &#123;&#123;evento&#125;&#125; irá acontecer dia
            &#123;&#123;data&#125;&#125;.
          </p>
        </AlertBox>
      )}

      {!bloqueado && paramsInvalidos.length > 0 && (
        <p style={{ fontSize: '0.8rem', color: 'red' }}>
          <span style={{ fontWeight: 'bold' }}>
            Parâmetros com nome inválido: {paramsInvalidos.join(', ')}.
            <br />
            Use letras minúsculas sem acento, números e "_". Exemplo: {`{{nome_cliente}}`}
          </span>
        </p>
      )}

      {!bloqueado && (fluxosAfetados.length > 0 || campanhasAfetadas.length > 0) && (
        <AlertBox variant="warning">
          <p>
            Os parâmetros do texto não batem com os usados abaixo. Depois de salvar,
            revise os nós de notificação (selecione o modelo de novo) e as campanhas.
          </p>
          <ul>
            {fluxosAfetados.map(f => (
              <li key={`f${f.id}`}>
                Fluxo <b>{f.nome}</b>
                {f.removidos.length > 0 && <> — usa parâmetros removidos: {f.removidos.join(', ')}</>}
                {f.novos.length > 0 && <> — não preenche: {f.novos.join(', ')}</>}
              </li>
            ))}
            {campanhasAfetadas.map(c => (
              <li key={`c${c.id}`}>
                Campanha <b>#{c.id}</b>
                {c.removidos.length > 0 && <> — usa parâmetros removidos: {c.removidos.join(', ')}</>}
                {c.novos.length > 0 && <> — não preenche: {c.novos.join(', ')}</>}
              </li>
            ))}
          </ul>
        </AlertBox>
      )}
      {!bloqueado && !!paramsValid && paramsList.length > 0 && (
        <>
          <div style={{ marginTop: '0.5rem' }}>
            <b>Exemplos</b>
          </div>
          <div style={{ fontStyle: 'italic', fontSize: '0.8rem', marginBottom: '1rem' }}>
            Para submeter à avaliação, precisamos incluir também amostras de
            dados que ocuparão o lugar dos parâmetros no conteúdo.
          </div>

          <CampoModeloMensagem
            texto={value?.conteudo}
            initialParams={value?.params || {}}
            onParamsChange={params => {
              onChange({
                ...value,
                params: { ...value?.params, ...params },
              });
            }}
            paramErrors={Object.entries(fieldErrors ?? {}).filter(([key]) => key.startsWith('params.')).reduce<Record<string, string>>((acc, [key, error]) => {
              const paramName = key.replace('params.', '');
              acc[paramName] = String(error);
              return acc;
            }, {})}
          />
          {fieldErrors?.params && (
            <p style={{ color: 'red', fontSize: '0.8rem' }}>
              <span style={{ fontWeight: 'bold' }}>
                {fieldErrors?.params}
              </span>
            </p>
          )}
        </>
      )}

      {!value?.id && (['whatsapp', 'wa360'].includes(selectedCanal?.tipo)) && (<GridItem sm={12}>
        <AlertBox variant="warning">
          <p>
            <b>ATENÇÃO:</b> A Meta reforçará as regras de classificação de modelos de mensagens.
            Conteúdo claramente de marketing mas enviadas como notificação / autenticação poderão sofrer sanções por parte da Meta, como:</p>
          <ul>
            <li>Rejeitar todas as mensagens de notificação</li>
            <li>Bloquear envio de modelos por 7 dias</li>
            <li>Estender o bloqueio em 30 dias em casos mais severos</li>
          </ul>
          <p>Seja sincero!</p>
        </AlertBox>
      </GridItem>
      )}

      {/* Botões de ação */}
      <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #dee2e6', borderRadius: '0.5rem' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Botões de ação <span style={{ fontWeight: 'normal', fontSize: '0.8rem', color: '#666' }}>(máx. 3 — os labels precisam ser enviados para aprovação da Meta)</span>
        </div>
        {(value?.botoes ?? []).map((botao: any, idx: number) => (
          <HBox key={idx} style={{ marginBottom: '0.5rem', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <CampoTexto
                label={`Botão ${idx + 1}`}
                value={botao.label}
                disabled={bloqueado}
                onChange={v => {
                  const novo = [...(value?.botoes ?? [])];
                  novo[idx] = { label: v };
                  onChange({ ...value, botoes: novo });
                }}
                maxLength={20}
                placeholder="Label do botão (máx. 20 chars)"
              />
            </div>
            {!bloqueado && (
              <Botao
                variant="secondary"
                type="button"
                icon={mdiTrashCan}
                onClick={() => {
                  const novo = (value?.botoes ?? []).filter((_: any, i: number) => i !== idx);
                  onChange({ ...value, botoes: novo });
                }}
              />
            )}
          </HBox>
        ))}
        {!bloqueado && (value?.botoes ?? []).length < 3 && (
          <Botao
            variant="secondary"
            type="button"
            icon={mdiPlus}
            onClick={() => {
              const novo = [...(value?.botoes ?? []), { label: '' }];
              onChange({ ...value, botoes: novo });
            }}
          >
            Adicionar botão
          </Botao>
        )}
        {bloqueado && (value?.botoes ?? []).length === 0 && (
          <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhum botão definido.</span>
        )}
      </div>
    </>
  );
};
